import {
  OnInit,
  OnDestroy,
  Directive,
  AfterViewInit,
  Injectable,
} from "@angular/core";
import {
  AsyncValidatorFn,
  FormArray,
  FormBuilder,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { Router } from "@angular/router";
import { IResultPageState } from "@common/models";
import { SpinnerService } from "@core/service";
import {
  distinctUntilChanged,
  Observable,
  of,
  skip,
  Subject,
  Subscription,
  takeUntil,
} from "rxjs";
import { InfoPopupComponent } from "src/app/core/components/popup/info-popup/info-popup.component";
import { IFormOptions } from "../models/forms.model";
import { IPopupScreenData } from "../models/popup-screen-data.model";

@Directive()
export abstract class BaseFpxFormComponent<
  Helper extends BaseFpxFormHelper<State>,
  State extends BaseFpxComponentState
> implements OnInit, OnDestroy, AfterViewInit, IEmbeddableForm
{
  /** @description Template variables */
  formGroup: FormGroup;

  state: State;

  formChangePublisher$ = new Subject<{ subject: string; payload: any }>();

  /** @description Private Variables */
  private readonly _syncValidators: ValidatorFn[] = [];
  private readonly _asyncValidators: AsyncValidatorFn[] = [];
  private readonly _formBuilder: FormBuilder;
  private readonly _route: Router;
  private readonly _helper: Helper;
  private _spinner: SpinnerService;
  private readonly notifyContext: Map<string, any> = new Map<string, any>();
  private readonly formControlsStatus$: Subscription[] = [];
  private readonly formControlsValue$: Subscription[] = [];
  private unSubcribe$ = new Subject();
  private _serviceCode: string;

  private formGroupStatus$!: Subscription;

  private routingParams: any;

  constructor(_formBuilder: FormBuilder, _route: Router, _helper: Helper) {
    this._formBuilder = _formBuilder;
    this._route = _route;
    this._helper = _helper;
    this._spinner = new SpinnerService();
    this.formGroup = this._formBuilder.group({}, { updateOn: "blur" });
    this.state = _helper.getState();
    this._setRoutingParams();
  }

  presentInViewMode(viewModeOptions: IViewModeOptions): void {
    throw new Error("Method not implemented.");
  }
  presentInEntryMode(entryModeOptions: IEntryModeOptions): void {
    throw new Error("Method not implemented.");
  }
  presentInRepairMode(repairModeOptions: IRepairModeOptions): void {
    throw new Error("Method not implemented.");
    //for each form-control . if reapair required, then editable, else read-only;
  }
  presentInApprovalMode(approvalModeOptions: IApprovalModeOptions): void {
    throw new Error("Method not implemented.");
  }

  ngAfterViewInit(): void {
    console.log("Inside ng after view init");
    this.doPostInit();
  }

  /** @description init lifecycle hook */
  ngOnInit(): void {
    this.doPreInit();
    this.formGroup.addValidators(this._syncValidators);
    this.formGroup.addAsyncValidators(this._asyncValidators);
    this.formGroup.updateValueAndValidity();

    this.formGroupStatus$ = this.formGroup.statusChanges.subscribe(
      (status: FormControlStatus) => {
        //console.log("Form Status Change", status);
      }
    );

    this._getRouterData();
  }

  /** @description destroy lifecycle hook */
  ngOnDestroy(): void {
    if (this.formGroupStatus$) {
      this.formGroupStatus$.unsubscribe();
    }
    while (this.formControlsStatus$.length > 0) {
      let subscription$ = this.formControlsStatus$.pop();
      if (subscription$) {
        subscription$.unsubscribe();
      }
    }
    while (this.formControlsValue$.length > 0) {
      let subscription$ = this.formControlsValue$.pop();
      if (subscription$) {
        subscription$.unsubscribe();
      }
    }
    this.doDestroy();
  }

  getErrors(): String[] | null {
    return Object.keys(this.formGroup.errors as ValidationErrors);
  }

  submit() {
    if (this._helper['preSubmitValidation']) {
      this._helper['preSubmitValidation'](this.formGroup).subscribe({
        next: (res:boolean) => {
          if (this.formGroup.valid) {
            this._spinner.show();
            this.doSubmit(this.formGroup);
          } else {
            this.formGroup.markAsTouched();
          }
        },
      });
    } else {
      if (this.formGroup.valid) {
        this._spinner.show();
        this.doSubmit(this.formGroup);
      } else {
        this.formGroup.markAsTouched();
      }
    }
  }
  confirm() {
    if (this.formGroup.disabled) {
      this._spinner.show();
      this.doConfirm(this.formGroup);
    } else {
      this.formGroup.markAsTouched();
    }
  }

  reset() {
    this.formGroup.reset();
    this.doReset(this.formGroup);
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  protected addSyncValidatorFn(syncValidatorFn: ValidatorFn) {
    this._syncValidators.push(syncValidatorFn);
  }

  protected addAsyncValidatorFn(asyncValidatorFn: AsyncValidatorFn) {
    this._asyncValidators.push(asyncValidatorFn);
  }

  protected addComponentState(name: string, defaultValue: any) {
    this.state[name] = defaultValue;
  }

  protected addFormControl(
    name: string,
    defaultValue: any,
    syncValidatorFns: ValidatorFn[] = [],
    asyncValidatorFns: AsyncValidatorFn[] = [],
    updateOn: "change" | "blur" | "submit" = "blur",
    initialDetection: number = 1
  ) {
    let formControl = this._formBuilder.control(defaultValue, {
      validators: syncValidatorFns,
      asyncValidators: asyncValidatorFns,
      updateOn: updateOn,
    });
    this.formGroup.addControl(name, formControl);
    formControl.statusChanges
      .pipe(takeUntil(this.unSubcribe$), skip(initialDetection))
      .subscribe((status: FormControlStatus) => {
        console.log("status change");
        /** vetri changes for dynamic function call for each form control 30-06-2022 */
        this._helper.notifyChangeHandler(
          name,
          status,
          formControl.value,
          this.formGroup
        );
      });
    formControl.valueChanges
      .pipe(takeUntil(this.unSubcribe$), distinctUntilChanged())
      .subscribe((value: any) => {
        // console.log("Control Value Change", name, value);
        this.formChangePublisher$.next({ subject: name, payload: value });
      });
  }

  protected addFormGroup(name: string, formGroup: FormGroup) {
    this.formGroup.addControl(name, formGroup);
    formGroup.statusChanges
      .pipe(takeUntil(this.unSubcribe$), distinctUntilChanged())
      .subscribe((status: FormControlStatus) => {
        // console.log("Group Status Change", name, status);
        // this.doStatusChange(name, status, formGroup.value, formGroup.value);
        /** vetri changes for dynamic function call for each form control 30-06-2022 */
        this._helper.notifyChangeHandler(
          name,
          status,
          formGroup.value,
          this.formGroup
        );
      });
    formGroup.valueChanges
      .pipe(takeUntil(this.unSubcribe$), distinctUntilChanged())
      .subscribe((value: any) => {
        console.log("Group Value Change", name, value);
        this.formChangePublisher$.next({ subject: name, payload: value });
      });
  }

  protected addFormArray(name: string, formArray: FormArray) {
    this.formGroup.addControl(name, formArray);
    formArray.statusChanges
      .pipe(takeUntil(this.unSubcribe$), distinctUntilChanged())
      .subscribe((status: FormControlStatus) => {
        console.log("Array Status Change", name, status);
        // this.doStatusChange(name, status, formGroup.value, formGroup.value);
        /** vetri changes for dynamic function call for each form control 30-06-2022 */
        this._helper.notifyChangeHandler(
          name,
          status,
          formArray.value,
          this.formGroup
        );
      });
    formArray.valueChanges
      .pipe(takeUntil(this.unSubcribe$), distinctUntilChanged())
      .subscribe((value: any) => {
        console.log("Array Value Change", name, value);
        this.formChangePublisher$.next({ subject: name, payload: value });
      });
  }

  /**
   * @param name string or string []
   * @param isDisable boolean
   * @param opts IformOptions to emit valuechanges on disable
   */
  protected disableFormControl(
    name: string | string[],
    isDisable: boolean,
    opts: IFormOptions = { emitEvent: false, onlySelf: false }
  ): void {
    try {
      if (typeof name === "string") {
        isDisable
          ? this.formGroup.get(name)?.disable(opts)
          : this.formGroup.get(name)?.enable(opts);
      } else if (Array.isArray(name)) {
        for (let i = 0; i < name.length; i++) {
          const element = name[i];
          isDisable
            ? this.formGroup.get(element)?.disable(opts)
            : this.formGroup.get(element)?.enable(opts);
        }
      }
    } catch (e: any) {
      throw new Error(e);
    }
  }

  protected resetFormControl(
    name: string | string[],
    opts: IFormOptions | undefined = { emitEvent: false, onlySelf: false }
  ): void {
    try {
      if (typeof name === "string") {
        this.formGroup.get(name)?.reset(null, opts);
      } else if (Array.isArray(name)) {
        for (let i = 0; i < name.length; i++) {
          const element = name[i];
          this.formGroup.get(element)?.reset(null, opts);
        }
      }
    } catch (e: any) {
      throw new Error(e);
    }
  }

  protected setFormData(formData: any) {
    this.formGroup.setValue(formData);
  }

  protected disableForm() {
    this.formGroup.disable();
  }

  protected getRoutingParam(key: string) {
    return this.routingParams ? this.routingParams[key] : "";
  }

  protected doReset(formGroup: FormGroup): void {}
  protected doSubmit(formGroup: FormGroup): void {}
  protected doConfirm(formGroup: FormGroup): void {}
  protected doPreInit(): void {}
  protected doPostInit(): void {}
  protected doDestroy(): void {}
  protected doStatusChange(
    subject: string,
    status: FormControlStatus,
    value: any,
    context: any
  ): void {}
  protected doChangeNotify(
    subject: string,
    payload: any,
    context: Map<string, any>
  ): void {}

  protected navigateToResultPage(
    resultContext: IResultPageState,
    loginNav?
  ): void {
    if (
      resultContext.resultPayload &&
      resultContext.resultPayload.httpStatus == 201
    ) {
      this.openResultPage(resultContext, (result) => {
        this._route.navigate(resultContext.resultPageUrl);
      });
    } else {
      if (loginNav) resultContext.resultPageUrl = ["login", "result"];
      resultContext.serviceCode = this._serviceCode;
      this._route.navigate(resultContext.resultPageUrl, {
        queryParams: resultContext,
        queryParamsHandling: "preserve",
      });
    }
  }
  openResultPage(resultContext, callBack = null) {
    let dialogRef;
    let _data: IPopupScreenData = {
      screenData: {
        flow: "postLogin",
        refNo: resultContext.resultPayload?.refNo,
      },
      screenTitle: "",
      component: "otp",
    };
    dialogRef = resultContext._dialog.open(InfoPopupComponent, {
      width: "100%",
      data: _data,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) callBack(result);
      console.log("The dialog was closed");
    });
  }

  private _getRouterData(): void {
    const navigationData = this._route.getCurrentNavigation();
    navigationData?.extras?.state
      ? this._presentData(navigationData?.extras?.state)
      : new Error("Method not implemented.");
  }

  private _presentData<T>(navigateState: T): void {}

  private _setRoutingParams(): void {
    this.routingParams =
      this._route.getCurrentNavigation()?.extras?.queryParams;
    this._serviceCode =
      this._route.getCurrentNavigation()?.extras?.state?.serviceCode || "";
  }
}

export interface IEmbeddableForm {
  presentInViewMode(viewModeOptions: IViewModeOptions): void;
  presentInEntryMode(entryModeOptions: IEntryModeOptions): void;
  presentInRepairMode(repairModeOptions: IRepairModeOptions): void;
  presentInApprovalMode(approvalModeOptions: IApprovalModeOptions): void;
}

export interface IDataLoadOptions {
  referenceDetails: any;
}

export interface IViewModeOptions extends IDataLoadOptions {}

export interface IEntryModeOptions {}

export interface IRepairModeOptions extends IDataLoadOptions {
  repairableFields: string[];
  repairableFieldComments: Map<string, string>;
}

export interface IApprovalModeOptions extends IDataLoadOptions {}

export interface INavigateState {
  presentMode: "VIEW" | "REPAIR" | "ENTRY" | "APPROVAL";
}

export abstract class BaseFpxComponentState {}

export interface BaseFpxChangeHandler {
  (
    name: string,
    status: FormControlStatus,
    value: any,
    formGroup: FormGroup
  ): void;
}

@Injectable()
export abstract class BaseFpxFormHelper<State extends BaseFpxComponentState> {
  protected state: State;

  protected handlerMap: Map<String, BaseFpxChangeHandler> = new Map();

  constructor(_state: State) {
    this.state = _state;
  }

  public getState(): State {
    return this.state;
  }

  protected addChangeHandler(
    name: String,
    changeHandler: BaseFpxChangeHandler
  ) {
    this.handlerMap.set(name, changeHandler);
  }

  public notifyChangeHandler(
    name: string,
    status: FormControlStatus,
    value: any,
    formGroup: FormGroup
  ) {
    if (this.handlerMap.has(name)) {
      this.handlerMap.get(name)(name, status, value, formGroup);
    }
  }


}
